import { captureMessage } from "@/error/sentry";
import type { Sticker, StickerColor, StickerIcon, StickerType } from "@/model/StickerType";
import { useStickerTypeStore } from "@/store/stickerType";

// The backend's `flag_type` wire format uses legacy short codes:
//   "flag" | "check" | "round" | "star" | "question" | "exclam"
// combined with a numeric color index (0..5), serialized as e.g. "exclam-3".
// These short codes are baked into existing card records in the database, so
// they cannot be renamed without a coordinated backend migration.
// The maps below translate to the new `Sticker` model only at this boundary.

const legacyIconToStickerIcon: Record<string, StickerIcon> = {
  flag: "flag",
  check: "check_mark",
  round: "dot",
  star: "star",
  question: "question_mark",
  exclam: "exclamation_mark",
};

const stickerIconToLegacyIcon: Record<StickerIcon, string> = {
  flag: "flag",
  check_mark: "check",
  dot: "round",
  star: "star",
  question_mark: "question",
  exclamation_mark: "exclam",
};

const colorIndexToStickerColor: Record<number, StickerColor> = {
  0: "red",
  1: "orange",
  2: "yellow",
  3: "blue",
  4: "purple",
  5: "green",
};

const stickerColorToColorIndex: Record<StickerColor, number> = {
  red: 0,
  orange: 1,
  yellow: 2,
  blue: 3,
  purple: 4,
  green: 5,
};

/**
 * Wire-format sentinel meaning "no sticker". Used by the search filter to
 * represent the "No sticker" option as a string member in the URL-synced Set.
 * `parseStickerString` decodes this back to `null`; the backend never receives
 * it (we send `null` instead — see `stickerToBackend`).
 */
const NO_STICKER_KEY = "none-0";

export function parseStickerString(value: string | null | undefined): Sticker | null {
  if (!value) return null;
  const [legacyIcon, colorIndexRaw] = value.split("-");
  if (legacyIcon === "none") return null;
  const icon = legacyIconToStickerIcon[legacyIcon];
  const color = colorIndexToStickerColor[Number(colorIndexRaw)];
  if (!icon || !color) {
    captureMessage("Failed to parse sticker wire format", { info: { value } });
    return null;
  }
  return { icon, color };
}

/**
 * Encode a sticker for the search-filter Set / URL. `null` means
 * "filter for cards without a sticker" and is encoded as the
 * {@link NO_STICKER_KEY} sentinel.
 */
export function stickerToFilterKey(sticker: Sticker | null): string {
  return sticker ? stickerToString(sticker)! : NO_STICKER_KEY;
}

/**
 * Encode a sticker for the backend wire format. `null` is sent as `null` —
 * meaning "clear the sticker on this card" or "no-sticker filter value" in
 * search queries.
 */
export function stickerToString(sticker: Sticker | null): string | null {
  if (!sticker) return null;
  return `${stickerIconToLegacyIcon[sticker.icon]}-${stickerColorToColorIndex[sticker.color]}`;
}

/**
 * Looks up a sticker type from the store by icon+color. Captures to Sentry if
 * the combination isn't in the store (data integrity issue) and returns
 * `undefined` so callers can render a fallback.
 */
export function findStickerType(sticker: Sticker | null): StickerType | undefined {
  if (!sticker) return undefined;
  const found = useStickerTypeStore().stickerTypes.find(
    (s) => s.icon === sticker.icon && s.color === sticker.color,
  );
  if (!found) {
    captureMessage("Sticker type not found in store", {
      info: { icon: sticker.icon, color: sticker.color },
    });
  }
  return found;
}
